import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '../../theme';
import { getApiUrl } from '../../config';
import {
  ArrowLeft,
  Building2,
  User,
  MapPin,
  Phone,
  Mail,
  AlertCircle,
  Check,
  Loader
} from 'lucide-react';

const AdminCreateClubPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Club form state
  const [clubData, setClubData] = useState({
    name: '',
    description: '',
    address: '',
    mapsUrl: '',
    city: '',
    province: '',
    phone: '',
    email: '',
    hasParking: false,
    hasBathrooms: true,
    hasChangingRooms: false,
    hasBuffet: false,
  });

  // Owner form state
  const [ownerData, setOwnerData] = useState({
    email: '',
    name: '',
    createNewOwner: false,
  });

  const [createdClub, setCreatedClub] = useState<{ id: string; name: string; slug: string } | null>(null);

  const handleClubChange = (field: string, value: string | boolean) => {
    setClubData((prev) => ({ ...prev, [field]: value }));
  };

  const handleOwnerChange = (field: string, value: string | boolean) => {
    setOwnerData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(getApiUrl('/admin/clubs'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...clubData,
          ownerEmail: ownerData.email,
          ownerName: ownerData.name,
          createNewOwner: ownerData.createNewOwner,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Error al crear el club');
      }

      setSuccess(true);
      setCreatedClub(data.club);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error desconocido');
    } finally {
      setLoading(false);
    }
  };

  const containerStyle: React.CSSProperties = {
    padding: '32px',
    maxWidth: '800px',
    margin: '0 auto',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'transparent',
    border: 'none',
    color: theme.colors.textSecondary,
    fontSize: theme.fonts.sizeBody,
    cursor: 'pointer',
    marginBottom: '24px',
    fontFamily: theme.fonts.body,
    padding: '8px 0',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '8px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.body,
    marginBottom: '32px',
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    borderRadius: theme.borderRadius.card,
    padding: '24px',
    border: `1px solid ${theme.colors.border}`,
    marginBottom: '24px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '1.2rem',
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    fontFamily: theme.fonts.heading,
    marginBottom: '20px',
  };

  const gridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
    gap: '16px',
  };

  const inputGroupStyle: React.CSSProperties = {
    marginBottom: '16px',
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
    marginBottom: '8px',
    fontFamily: theme.fonts.body,
  };

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px 16px',
    backgroundColor: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.input,
    color: theme.colors.text,
    fontSize: theme.fonts.sizeBody,
    fontFamily: theme.fonts.body,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const textareaStyle: React.CSSProperties = {
    ...inputStyle,
    minHeight: '100px',
    resize: 'vertical',
  };

  const checkboxContainerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
    cursor: 'pointer',
  };

  const checkboxStyle: React.CSSProperties = {
    width: '20px',
    height: '20px',
    accentColor: theme.colors.primary,
    cursor: 'pointer',
  };

  const buttonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    padding: '16px 32px',
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: theme.borderRadius.button,
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    cursor: loading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s ease',
    fontFamily: theme.fonts.body,
    opacity: loading ? 0.7 : 1,
  };

  const alertStyle = (type: 'error' | 'success'): React.CSSProperties => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '16px',
    borderRadius: theme.borderRadius.card,
    marginBottom: '24px',
    backgroundColor: type === 'error'
      ? 'rgba(255, 23, 68, 0.1)'
      : 'rgba(0, 230, 118, 0.1)',
    color: type === 'error' ? theme.colors.danger : theme.colors.primary,
    border: `1px solid ${type === 'error' ? theme.colors.danger : theme.colors.primary}`,
  });

  if (success && createdClub) {
    return (
      <div style={containerStyle}>
        <div style={{
          ...cardStyle,
          textAlign: 'center',
          padding: '48px',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '50%',
            backgroundColor: 'rgba(0, 230, 118, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 24px auto',
          }}>
            <Check size={40} color={theme.colors.primary} />
          </div>
          <h2 style={{
            ...titleStyle,
            fontSize: '1.5rem',
            marginBottom: '16px',
          }}>
            Club Creado Exitosamente
          </h2>
          <p style={{
            ...subtitleStyle,
            marginBottom: '8px',
          }}>
            {createdClub.name}
          </p>
          {createdClub.slug && (
            <p style={{
              fontSize: theme.fonts.sizeSmall,
              color: theme.colors.primary,
              marginBottom: '24px',
            }}>
              URL: /club/{createdClub.slug}
            </p>
          )}
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
            <button
              style={{
                ...buttonStyle,
                width: 'auto',
                backgroundColor: theme.colors.surface,
                color: theme.colors.text,
                border: `1px solid ${theme.colors.border}`,
              }}
              onClick={() => navigate('/admin/clubs')}
            >
              Ver Todos los Clubs
            </button>
            <button
              style={{ ...buttonStyle, width: 'auto' }}
              onClick={() => {
                setSuccess(false);
                setCreatedClub(null);
                setClubData({
                  name: '',
                  description: '',
                  address: '',
                  mapsUrl: '',
                  city: '',
                  province: '',
                  phone: '',
                  email: '',
                  hasParking: false,
                  hasBathrooms: true,
                  hasChangingRooms: false,
                  hasBuffet: false,
                });
                setOwnerData({
                  email: '',
                  name: '',
                  createNewOwner: false,
                });
              }}
            >
              Crear Otro Club
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={containerStyle}>
      <button
        style={backButtonStyle}
        onClick={() => navigate('/admin/clubs')}
      >
        <ArrowLeft size={20} />
        Volver a Clubs
      </button>

      <h1 style={titleStyle}>Crear Nuevo Club</h1>
      <p style={subtitleStyle}>
        Completa los datos del club y asigna un propietario
      </p>

      {error && (
        <div style={alertStyle('error')}>
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Club Information */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <Building2 size={24} color={theme.colors.primary} />
            Datos del Club
          </h3>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Nombre del Club *</label>
            <input
              type="text"
              value={clubData.name}
              onChange={(e) => handleClubChange('name', e.target.value)}
              style={inputStyle}
              placeholder="Ej: Complejo Deportivo La Cautiva"
              required
            />
          </div>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Descripcion</label>
            <textarea
              value={clubData.description}
              onChange={(e) => handleClubChange('description', e.target.value)}
              style={textareaStyle}
              placeholder="Breve descripcion del complejo..."
            />
          </div>

          <div style={gridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Direccion *</label>
              <input
                type="text"
                value={clubData.address}
                onChange={(e) => handleClubChange('address', e.target.value)}
                style={inputStyle}
                placeholder="Ej: Av. Corrientes 1234"
                required
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>URL de Google Maps *</label>
              <input
                type="url"
                value={clubData.mapsUrl}
                onChange={(e) => handleClubChange('mapsUrl', e.target.value)}
                style={inputStyle}
                placeholder="https://maps.google.com/..."
                required
              />
            </div>
          </div>

          <div style={gridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Ciudad *</label>
              <input
                type="text"
                value={clubData.city}
                onChange={(e) => handleClubChange('city', e.target.value)}
                style={inputStyle}
                placeholder="Ej: Buenos Aires"
                required
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Provincia *</label>
              <input
                type="text"
                value={clubData.province}
                onChange={(e) => handleClubChange('province', e.target.value)}
                style={inputStyle}
                placeholder="Ej: Buenos Aires"
                required
              />
            </div>
          </div>

          <div style={gridStyle}>
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Telefono</label>
              <input
                type="tel"
                value={clubData.phone}
                onChange={(e) => handleClubChange('phone', e.target.value)}
                style={inputStyle}
                placeholder="Ej: +54 11 1234-5678"
              />
            </div>

            <div style={inputGroupStyle}>
              <label style={labelStyle}>Email del Club</label>
              <input
                type="email"
                value={clubData.email}
                onChange={(e) => handleClubChange('email', e.target.value)}
                style={inputStyle}
                placeholder="contacto@club.com"
              />
            </div>
          </div>

          <div style={{ marginTop: '16px' }}>
            <label style={{ ...labelStyle, marginBottom: '12px' }}>Amenities</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '8px' }}>
              <label style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  checked={clubData.hasParking}
                  onChange={(e) => handleClubChange('hasParking', e.target.checked)}
                  style={checkboxStyle}
                />
                <span style={{ color: theme.colors.text }}>Estacionamiento</span>
              </label>
              <label style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  checked={clubData.hasBathrooms}
                  onChange={(e) => handleClubChange('hasBathrooms', e.target.checked)}
                  style={checkboxStyle}
                />
                <span style={{ color: theme.colors.text }}>Banos</span>
              </label>
              <label style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  checked={clubData.hasChangingRooms}
                  onChange={(e) => handleClubChange('hasChangingRooms', e.target.checked)}
                  style={checkboxStyle}
                />
                <span style={{ color: theme.colors.text }}>Vestuarios</span>
              </label>
              <label style={checkboxContainerStyle}>
                <input
                  type="checkbox"
                  checked={clubData.hasBuffet}
                  onChange={(e) => handleClubChange('hasBuffet', e.target.checked)}
                  style={checkboxStyle}
                />
                <span style={{ color: theme.colors.text }}>Buffet</span>
              </label>
            </div>
          </div>
        </div>

        {/* Owner Information */}
        <div style={cardStyle}>
          <h3 style={sectionTitleStyle}>
            <User size={24} color={theme.colors.primary} />
            Datos del Propietario
          </h3>

          <div style={inputGroupStyle}>
            <label style={labelStyle}>Email del Propietario *</label>
            <input
              type="email"
              value={ownerData.email}
              onChange={(e) => handleOwnerChange('email', e.target.value)}
              style={inputStyle}
              placeholder="propietario@email.com"
              required
            />
          </div>

          <label style={checkboxContainerStyle}>
            <input
              type="checkbox"
              checked={ownerData.createNewOwner}
              onChange={(e) => handleOwnerChange('createNewOwner', e.target.checked)}
              style={checkboxStyle}
            />
            <span style={{ color: theme.colors.text }}>
              Crear nuevo usuario (si no existe)
            </span>
          </label>

          {ownerData.createNewOwner && (
            <div style={inputGroupStyle}>
              <label style={labelStyle}>Nombre del Propietario</label>
              <input
                type="text"
                value={ownerData.name}
                onChange={(e) => handleOwnerChange('name', e.target.value)}
                style={inputStyle}
                placeholder="Nombre completo"
              />
            </div>
          )}

          <p style={{
            fontSize: theme.fonts.sizeSmall,
            color: theme.colors.textSecondary,
            marginTop: '16px',
            padding: '12px',
            backgroundColor: theme.colors.background,
            borderRadius: theme.borderRadius.input,
          }}>
            Si el usuario ya existe, se le asignara el club automaticamente y
            su rol cambiara a VENUE_OWNER. Si no existe y marcas "Crear nuevo usuario",
            se creara una cuenta nueva.
          </p>
        </div>

        <button
          type="submit"
          style={buttonStyle}
          disabled={loading}
          onMouseEnter={(e) => {
            if (!loading) e.currentTarget.style.backgroundColor = '#00D96F';
          }}
          onMouseLeave={(e) => {
            if (!loading) e.currentTarget.style.backgroundColor = theme.colors.primary;
          }}
        >
          {loading ? (
            <>
              <Loader size={20} className="animate-spin" />
              Creando...
            </>
          ) : (
            <>
              <Building2 size={20} />
              Crear Club
            </>
          )}
        </button>
      </form>
    </div>
  );
};

export default AdminCreateClubPage;
